import React from 'react';
import { Link } from 'react-router-dom';
import {
  FaTrophy,
  FaUsers,
  FaCalendarAlt,
  FaStar,
  FaArrowRight,
  FaCheckCircle,
  FaQuoteLeft,
  FaPlay
} from 'react-icons/fa';
import './Home.css';

const Home = () => {
  const stats = [
    { number: '15+', label: 'Years Experience', icon: <FaTrophy /> },
    { number: '5000+', label: 'Happy Members', icon: <FaUsers /> },
    { number: '12', label: 'Professional Courts', icon: <FaCalendarAlt /> },
    { number: '50+', label: 'Tournaments Won', icon: <FaStar /> }
  ];

  const services = [
    {
      icon: '🏸',
      title: 'Court Rental',
      description: 'Book our premium badminton courts with professional-grade flooring and lighting.',
      link: '/court-rental'
    },
    {
      icon: '🎯',
      title: 'Training Programs',
      description: 'Expert coaching for all skill levels from beginners to competitive players.',
      link: '/training'
    },
    {
      icon: '👥',
      title: 'Membership',
      description: 'Join our community and enjoy exclusive benefits, discounts, and priority booking.',
      link: '/membership'
    },
    {
      icon: '🏆',
      title: 'Tournaments',
      description: 'Participate in regular tournaments and competitions to test your skills.',
      link: '/events'
    }
  ];

  const features = [
    'State-of-the-art facilities with 12 professional courts',
    'Certified coaches with international experience',
    'Programs for all ages and skill levels',
    'Modern equipment and professional flooring',
    'Air-conditioned indoor facility',
    'Pro shop with quality equipment'
  ];

  const testimonials = [
    {
      name: 'Aarav Mehta',
      role: 'Intermediate Player',
      rating: 5,
      text: 'Net Nest Badminton Sports Club has completely transformed my game. The coaches focus deeply on fundamentals and footwork, which helped me gain confidence during matches. The training environment is disciplined yet motivating.',
      category: 'Training'
    },
    {
      name: 'Riya Sharma',
      role: 'Beginner Player',
      rating: 5,
      text: 'I joined Net Nest as a beginner with no prior experience. The coaches were patient and supportive, helping me learn step by step. Today, I feel confident playing competitive rallies.',
      category: 'Facilities'
    },
    {
      name: 'Rohit Verma',
      role: 'Advanced Player',
      rating: 5,
      text: 'The professional coaching at Net Nest is outstanding. Match strategies, fitness training, and mental preparation are given equal importance. This club truly focuses on overall player development.',
      category: 'Junior Academy'
    },
  ];

  const upcomingEvents = [
    {
      id: 1,
      title: 'Intra-Club Badminton Tournament',
      date: 'January 15-16, 2026',
      time: '9:00 AM - 6:00 PM',
      location: 'Net Nest Main Hall',
      description: 'This event is organized exclusively for Net Nest members. It allows players to compete with fellow trainees in a friendly yet competitive environment. The tournament helps players apply their training in real match situations and gain confidence.',
      category: 'Tournament',
      participants: '128 spots',
      fee: '$45',
      image: 'https://images.unsplash.com/photo-1626224583764-f87db24ac4ea?w=600',
      featured: true
    },
    {
      id: 2,
      title: 'Junior Badminton Championship',
      date: 'January 22, 2026',
      time: '10:00 AM - 12:00 PM',
      location: 'Courts 1-4',
      description: 'Designed for young players, this championship encourages early competitive exposure. Age-wise categories ensure fair play while helping children develop discipline, confidence, and sportsmanship.',
      category: 'Workshop',
      participants: '24 spots',
      fee: 'Free',
      image: 'https://images.unsplash.com/photo-1599474924187-334a4ae5bd3c?w=600',
      featured: false
    },
    {
      id: 3,
      title: 'Open Badminton Tournament',
      date: 'February 5, 2026',
      time: '1:00 PM - 5:00 PM',
      location: 'Net Nest Main Hall',
      description: 'The Open Badminton Tournament welcomes players from other clubs and academies. This event provides high-level competition, helping our players measure their skills against different playing styles.',
      category: 'Tournament',
      participants: '32 players',
      fee: 'Free to watch',
      image: 'https://images.unsplash.com/photo-1626224583764-f87db24ac4ea?w=600',
      featured: false
    },
  ];

  return (
    <div className="home">
      {/* Hero Section */}
      <section className="hero">
        <div className="hero-overlay"></div>
        <div className="hero-content">
          <div className="container">
            <span className="hero-badge">Welcome to Net Nest</span>
            <h1>Where Champions <span>Are Made</span></h1>
            <p>
              Experience world-class badminton facilities, professional coaching,
              and a vibrant community dedicated to excellence in the sport we love.
            </p>
            <div className="hero-buttons">
              <Link to="/contact" className="btn btn-primary">
                Join Our Club <FaArrowRight />
              </Link>
              <Link to="/about" className="btn btn-outline">
                <FaPlay /> Watch Video
              </Link>
            </div>
          </div>
        </div>
        <div className="hero-stats">
          <div className="container">
            <div className="stats-grid">
              {stats.map((stat, index) => (
                <div className="stat-item" key={index}>
                  <div className="stat-icon">{stat.icon}</div>
                  <div className="stat-content">
                    <h3>{stat.number}</h3>
                    <p>{stat.label}</p>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* About Section */}
      <section className="home-about section">
        <div className="container">
          <div className="about-grid">
            <div className="about-images">
              <div className="image-main">
                <img src="https://images.unsplash.com/photo-1626224583764-f87db24ac4ea?w=600" alt="Badminton Court" />
              </div>
              <div className="image-secondary">
                <img src="https://images.unsplash.com/photo-1599474924187-334a4ae5bd3c?w=400" alt="Training Session" />
              </div>
              <div className="experience-badge">
                <span className="years">15+</span>
                <span className="text">Years of Excellence</span>
              </div>
            </div>
            <div className="about-content">
              <span className="section-badge">About Us</span>
              <h2>Premier Badminton Excellence Since 2009</h2>
              <p>
                Net Nest Badminton Sports Club is a dedicated badminton training and development center committed to excellence in sports, fitness, and personal growth. Established with the vision of promoting badminton at all levels, our club provides a professional and structured environment where players can learn, train, and excel with confidence.
              </p>
              <p>
                At Net Nest, we believe badminton is more than just a sport—it is a pathway to discipline, resilience, teamwork, and a healthy lifestyle. Our club welcomes players of all age groups and skill levels, from beginners discovering the basics to advanced athletes preparing for competitive tournaments.
              </p>
              <ul className="features-list">
                {features.slice(0, 4).map((feature, index) => (
                  <li key={index}>
                    <FaCheckCircle /> {feature}
                  </li>
                ))}
              </ul>
              <Link to="/about" className="btn btn-primary">
                Learn More About Us <FaArrowRight />
              </Link>
            </div>
          </div>
        </div>
      </section>

      {/* Services Section */}
      <section className="home-services section">
        <div className="container">
          <div className="section-header center">
            <span className="section-badge">Our Services</span>
            <h2>What We Offer</h2>
            <p>Comprehensive badminton services designed to meet your every need</p>
          </div>
          <div className="services-grid">
            {services.map((service, index) => (
              <div className="service-card" key={index}>
                <div className="service-icon">{service.icon}</div>
                <h3>{service.title}</h3>
                <p>{service.description}</p>
                <Link to={service.link} className="service-link">
                  Learn More <FaArrowRight />
                </Link>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Why Choose Us Section */}
      <section className="why-choose section">
        <div className="container">
          <div className="why-choose-grid">
            <div className="why-content">
              <span className="section-badge">Why Choose Us</span>
              <h2>Your Journey to Badminton Excellence Starts Here</h2>
              <p>
                At Net Nest, we're committed to providing an unparalleled badminton
                experience. Here's why hundreds of players choose us as their home court.
              </p>
              <ul className="why-list">
                {features.map((feature, index) => (
                  <li key={index}>
                    <FaCheckCircle /> {feature}
                  </li>
                ))}
              </ul>
              <Link to="/why-choose-us" className="btn btn-primary">
                Discover More <FaArrowRight />
              </Link>
            </div>
            <div className="why-image">
              <img src="img/Advanced & Competitive Training Program.jpg" alt="Professional Training" />
            </div>
          </div>
        </div>
      </section>

      {/* Testimonials Section */}
      <section className="home-testimonials section">
        <div className="container">
          <div className="section-header center">
            <span className="section-badge">Testimonials</span>
            <h2>What Our Members Say</h2>
            <p>Hear from the amazing community that makes Net Nest special</p>
          </div>
          <div className="testimonials-grid">
            {testimonials.map((testimonial, index) => (
              <div className="testimonial-card" key={index}>
                <FaQuoteLeft className="quote-icon" />
                <p className="testimonial-text">{testimonial.text}</p>
                <div className="testimonial-author">
                  <div>
                    <h4>{testimonial.name}</h4>
                    <span>{testimonial.role}</span>
                  </div>
                </div>
              </div>
            ))}
          </div>
          <div className="section-footer">
            <Link to="/testimonials" className="btn btn-outline-dark">
              View All Testimonials <FaArrowRight />
            </Link>
          </div>
        </div>
      </section>

      {/* Events Section */}
      <section className="home-events section">
        <div className="container">
          <div className="events-header">
            <div>
              <span className="section-badge">Upcoming Events</span>
              <h2>Join Our Events</h2>
            </div>
            <Link to="/events" className="btn btn-outline">
              View All Events <FaArrowRight />
            </Link>
          </div>
          <div className="events-grid">
            {upcomingEvents.map((event, index) => (
              <div className="event-card" key={index}>
                <div className="event-date">
                  <span className="day">{event.date.day}</span>
                  <span className="month">{event.date.month}</span>
                </div>
                <div className="event-content">
                  <span className="event-category">{event.category}</span>
                  <h3>{event.title}</h3>
                  <p>{event.description}</p>
                </div>
              </div>
            ))}
          </div>
          <div className="section-footer">
            <Link to="/events" className="btn btn-outline-dark">
              View All Events <FaArrowRight />
            </Link>
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="cta-section">
        <div className="container">
          <div className="cta-content">
            <h2>Ready to Start Your Badminton Journey?</h2>
            <p>
              Join Net Nest today and experience the difference. Whether you're a
              beginner or a pro, we have something for everyone.
            </p>
            <div className="cta-buttons">
              <Link to="/membership" className="btn btn-white">
                Become a Member
              </Link>
              <Link to="/contact" className="btn btn-outline-white">
                Contact Us
              </Link>
            </div>
          </div>
        </div>
      </section>
    </div>
  );
};

export default Home;
